import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import { Language } from "../../../types";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import Input from "../../ui/Input";
import LoadingSpinner from "../../ui/LoadingSpinner";
import SectionCard from "../../ui/SectionCard";
import Sections from "../../ui/Sections";
import { CustomSelect } from "../../ui/select/CustomSelect";
import Textarea from "../../ui/Textarea";
import styles from "./BorderGeneralData.module.css";

export interface BorderGeneralDataFormBody {
  name: string;
  description: string | null;
  language: Language | null;
}

interface BorderGeneralDataProps {
  defaultValues?: BorderGeneralDataFormBody;
  languages: Language[];
  isLoading?: boolean;
  next: () => void;
  onSubmit: (body: BorderGeneralDataFormBody) => Promise<void> | undefined;
  isAdmin?: boolean;
  canEdit?: boolean;
}

const BorderGeneralData: FC<BorderGeneralDataProps> = ({
  defaultValues,
  languages,
  isLoading,
  next,
  onSubmit,
  isAdmin,
  canEdit,
}: BorderGeneralDataProps) => {
  const i18n = useI18n();

  return (
    <Form<BorderGeneralDataFormBody>
      mode={"onBlur"}
      defaultValues={defaultValues}
      disabled={isLoading}
      onSubmit={() => async (formData) =>
        isAdmin ? await onSubmit(formData) : canEdit ? await onSubmit(formData)?.then(() => next()) : next()
      }
      secondaryButton={isLoading && <LoadingSpinner delayed={0} size="small" />}
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
    >
      {({ register, setValue, control, formState: { errors }, reset }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [defaultValues]);

        return (
          <Sections columns={2}>
            <SectionCard>
              <div className={styles.form}>
                <FormField label={i18n.translation.common.name} error={errors.name}>
                  {({ labelId, isInvalid, isOptional }) => (
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isLoading}
                      readOnly={!canEdit}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                      onBlur={(e) => {
                        setValue("name", e.target.value);
                      }}
                    />
                  )}
                </FormField>
                <Controller
                  name="language"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={defaultValues?.language}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.languages.singular} error={fieldState.error}>
                      {({ labelId }) => (
                        <CustomSelect
                          {...field}
                          inputId={labelId}
                          options={languages}
                          placeholder={`${i18n.translation.common.select}...`}
                          isDisabled={field.disabled || isLoading || !canEdit}
                          isLoading={isLoading}
                          getOptionLabel={(x) => x.display}
                          isInvalid={fieldState.invalid}
                          onChange={(value) => {
                            setValue("language", value ?? null);
                          }}
                        />
                      )}
                    </FormField>
                  )}
                />
                <FormField label={i18n.translation.common.description} error={errors.description} isOptional>
                  {({ labelId, isInvalid, isOptional }) => (
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={!canEdit}
                      {...register("description", { required: !isOptional })}
                      onBlur={(e) => {
                        if (defaultValues?.description !== e.target.value) {
                          setValue("description", e.target.value);
                        }
                      }}
                    />
                  )}
                </FormField>
              </div>
            </SectionCard>
            <div className={styles.borderImage}>
              <img src={window.location.origin + "/border_image.png"} />
            </div>
          </Sections>
        );
      }}
    </Form>
  );
};

export default BorderGeneralData;
